import dayjs from "dayjs";
import type { InternalRequestEventLogDto } from "@synthesis/shared-services/internal/application/types";
import type { RequestEventLogDto } from "@synthesis/shared-services/external/application/types";

import { APPLICATION_STATUS_FALLBACK_LABEL, type ApplicationStatus } from "../../constants";

export type ApplicationEventLogEntry = InternalRequestEventLogDto | RequestEventLogDto;

export type ApplicationDetailHistoryEntry = {
  id: string;
  userName: string;
  userEmail?: string | null;
  userInitials: string;
  action: string;
  occurredAt: string;
  tooltip?: string;
};

type ApplicationHistoryActorInfo = NonNullable<InternalRequestEventLogDto["actorInfo"]>;

const EVENT_TYPE_ACTION_LABEL: Record<string, string> = {
  // Internal — status transitions
  INTAKE_PASS: "направил заявку в аппарат НГ",
  REQUEST_REVISION: "вернул заявку на доработку",
  REJECT: "оформил мотивированный отказ",
  TAKE_NG: "взял заявку в работу",
  SEND_TO_NTP: "направил заявку в НТП",
  TAKE_NTP: "взял заявку в работу",
  ASSIGN_NTP_RESP: "назначил ответственного в НТП",
  START_COMMISSION_PREP: "начал подготовку комиссии",
  START_DECISION: "перешёл к формированию решения",
  SUBMIT_NTP_APPROVAL: "отправил решение на согласование НТП",
  NTP_REWORK: "вернул решение на доработку",
  NTP_APPROVE: "согласовал решение НТП",
  NG_REWORK: "вернул решение на доработку",
  NG_APPROVE: "выдал ТУ",
  REWORK_RESUBMIT: "отправил решение на согласование повторно",
  // Internal — assignments
  ASSIGN: "назначил сотрудника",
  REASSIGN: "переназначил сотрудника",
  // Internal — import & exchange
  IMPORTED: "заявка импортирована",
  IMPORT_UPDATED: "заявка обновлена при импорте",
  RESUMED: "заявка возвращена из доработки",
  EXPORT_RESULT: "результат экспортирован",
  // Internal — payment
  INVOICE_ISSUED: "выставил счёт",
  INVOICE_UPDATED: "обновил счёт",
  INVOICE_SENT: "отправил счёт",
  PAYMENT_AWAITING: "ожидает оплаты",
  PAYMENT_FLAG_SET: "изменил статус оплаты",
  PAYMENT_CONFIRMED: "подтвердил оплату",
  // External
  SUBMIT: "отправил заявку",
  DELETE: "удалил заявку",
  EDIT: "изменил заявку",
  PAYMENT_PROOF_UPLOADED: "загрузил подтверждение оплаты",
  PAYMENT_PROOF_DELETED: "удалил подтверждение оплаты",
  PAYMENT_NOTIFIED: "уведомил об оплате",
  EXPORT: "экспортировал заявку",
  IMPORT_RESULT: "получен результат импорта",
  INVOICE_IMPORTED: "получен счёт",
};

const PAYMENT_FLAG_LABEL: Record<string, string> = {
  UNDER_REVIEW: "На рассмотрении",
  NO_PAYMENT: "Не оплачено",
  PAID: "Оплачено",
};

const SLOT_LABEL: Record<string, string> = {
  NG_STAFF: "Сотрудник аппарата НГ",
  NTP_HEAD: "Руководитель НТП",
  NTP_RESPONSIBLE: "Ответственный НТП",
};

const DECISION_LABEL: Record<string, string> = {
  APPROVED: "Согласовано",
  REJECTED: "Отклонено",
};

const OUTCOME_LABEL: Record<string, string> = {
  APPROVED: "Согласовано",
  REJECTED: "Отклонено",
};

const ACTOR_ROLE_LABEL: Record<string, string> = {
  OPERATOR: "Оператор",
  APPLICANT: "Заявитель",
  NG_SECRETARY: "Секретарь НГ",
  NG_OFFICE: "Аппарат НГ",
  NTP_CHIEF: "Начальник НТП",
  NTP_RESPONSIBLE: "Ответственный НТП",
  APPROVER: "Согласующий",
};

const formatDateTime = (value?: string | null): string => (value ? dayjs(value).format("DD.MM.YYYY, HH:mm") : "—");

const getEventActorInfo = (event: ApplicationEventLogEntry): ApplicationHistoryActorInfo | null | undefined =>
  "actorInfo" in event ? event.actorInfo : undefined;

const getInitialsFromParts = (lastName?: string | null, firstName?: string | null, fallbackName?: string): string => {
  const lastInitial = lastName?.trim()?.[0]?.toUpperCase();
  const firstInitial = firstName?.trim()?.[0]?.toUpperCase();

  if (lastInitial || firstInitial) {
    return `${lastInitial ?? "-"}${firstInitial ?? "-"}`;
  }

  const parts = fallbackName?.trim().split(/\s+/).filter(Boolean) ?? [];

  if (parts.length === 0) {
    return "??";
  }

  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }

  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
};

const getActorInfoFullName = (actorInfo?: ApplicationHistoryActorInfo | null): string | null => {
  const fullName = [actorInfo?.lastName, actorInfo?.firstName, actorInfo?.middleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  return fullName || null;
};

const getPayloadString = (payload: Record<string, unknown> | null | undefined, ...keys: string[]): string | null => {
  if (!payload) {
    return null;
  }

  for (const key of keys) {
    const value = payload[key];

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
};

const normalizeEventType = (eventType?: string | null): string | null => {
  const normalized = eventType?.trim().toUpperCase();

  return normalized || null;
};

const getStatusLabel = (status?: string | null): string | null => {
  if (!status?.trim()) {
    return null;
  }

  const label = APPLICATION_STATUS_FALLBACK_LABEL[status as ApplicationStatus];

  return label ?? status;
};

const formatEventTooltip = (eventType: string | null | undefined, payload: Record<string, unknown> | null | undefined): string | null => {
  const type = (eventType ?? "").trim().toUpperCase();
  const p = payload ?? {};

  switch (type) {
    case "INTAKE_PASS":
    case "TAKE_NG":
    case "SEND_TO_NTP":
    case "TAKE_NTP":
    case "ASSIGN_NTP_RESP":
    case "START_COMMISSION_PREP":
    case "START_DECISION":
    case "SUBMIT_NTP_APPROVAL":
    case "NTP_REWORK":
    case "NTP_APPROVE":
    case "NG_REWORK":
    case "NG_APPROVE":
    case "REWORK_RESUBMIT": {
      const fromStatus = getStatusLabel(String(p.from ?? ""));
      const toStatus = getStatusLabel(String(p.to ?? ""));
      const parts: string[] = [];

      if (fromStatus && toStatus) {
        parts.push(`${fromStatus} → ${toStatus}`);
      }

      if (p.decision && DECISION_LABEL[String(p.decision)]) {
        parts.push(`Решение: ${DECISION_LABEL[String(p.decision)]}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "REQUEST_REVISION": {
      const fromStatus = getStatusLabel(String(p.from ?? ""));
      const toStatus = getStatusLabel(String(p.to ?? ""));
      const parts: string[] = [];

      if (fromStatus && toStatus) {
        parts.push(`${fromStatus} → ${toStatus}`);
      }

      if (p.returnStage) {
        parts.push(`Этап возврата: ${getStatusLabel(String(p.returnStage)) ?? p.returnStage}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "REJECT": {
      if (p.from && p.to) {
        const fromStatus = getStatusLabel(String(p.from));
        const toStatus = getStatusLabel(String(p.to));
        const parts: string[] = [];

        if (fromStatus && toStatus) {
          parts.push(`${fromStatus} → ${toStatus}`);
        }

        if (p.decision && DECISION_LABEL[String(p.decision)]) {
          parts.push(`Решение: ${DECISION_LABEL[String(p.decision)]}`);
        }

        return parts.length ? parts.join("\n") : null;
      }

      if (p.operatorState) {
        return `Причина: ${p.operatorState}`;
      }

      return null;
    }
    case "ASSIGN":
    case "REASSIGN": {
      const parts: string[] = [];

      if (p.slot) {
        const slotLabel = SLOT_LABEL[String(p.slot)] ?? p.slot;
        parts.push(`Роль: ${slotLabel}`);
      }

      if (p.reason) {
        parts.push(`Причина: ${p.reason}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "IMPORTED":
    case "IMPORT_UPDATED":
    case "RESUMED": {
      const parts: string[] = [];

      if (p.number != null) {
        parts.push(`Номер: ${p.number}`);
      }

      if (p.filesImported != null) {
        parts.push(`Файлов импортировано: ${p.filesImported}`);
      }

      if (p.commentsImported != null) {
        parts.push(`Комментариев импортировано: ${p.commentsImported}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "INVOICE_ISSUED":
    case "INVOICE_UPDATED": {
      const parts: string[] = [];

      if (p.invoiceNumber) {
        parts.push(`Номер счёта: ${p.invoiceNumber}`);
      }

      if (p.amount != null) {
        parts.push(`Сумма: ${p.amount}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "PAYMENT_FLAG_SET": {
      const parts: string[] = [];

      if (p.from) {
        parts.push(`Было: ${PAYMENT_FLAG_LABEL[String(p.from)] ?? p.from}`);
      }

      if (p.to) {
        parts.push(`Стало: ${PAYMENT_FLAG_LABEL[String(p.to)] ?? p.to}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "PAYMENT_AWAITING": {
      if (p.reason === "NG_REWORK") {
        return "Причина: доработка НГ";
      }

      return null;
    }
    case "PAYMENT_CONFIRMED": {
      if (p.proofFileName) {
        return `Файл: ${p.proofFileName}`;
      }

      if (p.externalRequestId) {
        return `Номер заявки: ${p.externalRequestId}`;
      }

      return null;
    }
    case "IMPORT_RESULT": {
      const parts: string[] = [];
      if (p.number != null) {
        parts.push(`Номер: ${p.number}`);
      }

      if (p.outcome) {
        const outcomeLabel = OUTCOME_LABEL[String(p.outcome)] ?? p.outcome;
        parts.push(`Результат: ${outcomeLabel}`);
      }

      if (p.filesImported != null) {
        parts.push(`Файлов импортировано: ${p.filesImported}`);
      }

      if (p.commentsImported != null) {
        parts.push(`Комментариев импортировано: ${p.commentsImported}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    case "PAYMENT_PROOF_UPLOADED": {
      if (p.fileName) {
        return `Файл: ${p.fileName}`;
      }

      return null;
    }
    case "INVOICE_IMPORTED": {
      const parts: string[] = [];
      if (p.number != null) {
        parts.push(`Номер заявки: ${p.number}`);
      }

      if (p.invoiceNumber) {
        parts.push(`Номер счёта: ${p.invoiceNumber}`);
      }

      if (p.amount != null) {
        parts.push(`Сумма: ${p.amount}`);
      }

      return parts.length ? parts.join("\n") : null;
    }
    default:
      return null;
  }
};

export const getApplicationHistoryActionLabel = (eventType?: string | null, payload?: Record<string, unknown> | null): string => {
  const normalizedEventType = normalizeEventType(eventType);

  if (normalizedEventType && EVENT_TYPE_ACTION_LABEL[normalizedEventType]) {
    return EVENT_TYPE_ACTION_LABEL[normalizedEventType];
  }

  const fromStatus = getStatusLabel(getPayloadString(payload, "from", "fromStatus"));
  const toStatus = getStatusLabel(getPayloadString(payload, "to", "toStatus", "status"));

  if (fromStatus && toStatus) {
    return `изменил статус: ${fromStatus} → ${toStatus}`;
  }

  if (toStatus) {
    return `изменил статус на «${toStatus}»`;
  }

  return normalizedEventType ?? "выполнил действие";
};

const getEventLogActorName = (event: ApplicationEventLogEntry): string => {
  const actorInfoName = getActorInfoFullName(getEventActorInfo(event));

  if (actorInfoName) {
    return actorInfoName;
  }

  const actorName = getPayloadString(
    event.payload as Record<string, unknown> | null | undefined,
    "actorName",
    "actorDisplay",
    "userName",
    "displayName",
  );

  if (actorName) {
    return actorName;
  }

  const normalizedRole = event.actorRole?.trim().toUpperCase();

  if (normalizedRole && ACTOR_ROLE_LABEL[normalizedRole]) {
    return ACTOR_ROLE_LABEL[normalizedRole];
  }

  if (typeof event.actorRole === "string" && event.actorRole.trim()) {
    return event.actorRole;
  }

  return "Пользователь";
};

export const buildApplicationHistoryFromEventLogs = (events: ReadonlyArray<ApplicationEventLogEntry>): ApplicationDetailHistoryEntry[] =>
  events.map((event, index) => {
    const userName = getEventLogActorName(event);
    const actorInfo = getEventActorInfo(event);

    return {
      id: event.id ?? `${event.eventType ?? "event"}-${index}`,
      userName,
      userEmail: actorInfo?.email?.trim() || null,
      userInitials: getInitialsFromParts(actorInfo?.lastName, actorInfo?.firstName, userName),
      action: getApplicationHistoryActionLabel(event.eventType, event.payload as Record<string, unknown> | null),
      occurredAt: formatDateTime(event.occurredAt),
      tooltip: formatEventTooltip(event.eventType, event.payload as Record<string, unknown> | null) ?? undefined,
    };
  });

const REVISION_HISTORY_EVENT_TYPES = new Set(["REQUEST_REVISION"]);

export const isApplicationReturnedAfterApplicantRevision = (events: ReadonlyArray<ApplicationEventLogEntry>): boolean => {
  let importCount = 0;

  for (const event of events) {
    const eventType = normalizeEventType(event.eventType);

    if (eventType === "IMPORTED") {
      importCount += 1;

      if (importCount > 1) {
        return true;
      }
    }

    if (eventType && REVISION_HISTORY_EVENT_TYPES.has(eventType)) {
      return true;
    }
  }

  return false;
};

export const isApplicationNgApprovalAfterNtpRework = (events: ReadonlyArray<ApplicationEventLogEntry>): boolean =>
  events.some((event) => normalizeEventType(event.eventType) === "NG_REWORK");

export const isApplicationNtpApprovalAfterNtpRework = (events: ReadonlyArray<ApplicationEventLogEntry>): boolean =>
  events.some((event) => normalizeEventType(event.eventType) === "NTP_REWORK");
